// Every URL the frontend recognises, written down once. The app has no router
// dep (CLAUDE.md), so this module is the one place that knows each shape:
// App.jsx reads it to pick a surface, AppShell.jsx to route within the
// signed-in app, and the receipt email builds the admin result path
// server-side (app/services/notify.py result_url). Keep them in step.
//
//   /s/<token>                                   public link page
//   /verify/<token>                              magic sign-in landing
//   /p/<token>                                   principal's personal link (#13)
//   /admin                                       admin home: the spaces list
//   /admin/spaces/<space_id>[/results|/settings]  one space: People (default), Results or Settings
//   /admin/templates                             template editor (admins only)
//   /admin/spaces/<space_id>/results/<link_id>[/<n>]  a call's write-up, in its space
//   /discovery                                   principal home: their spaces
//   /discovery/<space_id>                        one space
//   /discovery/<space_id>/results                that space's results
//   /discovery/<space_id>/results/<link_id>[/<n>]  a call's write-up, in its space

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
// URL-safe base64, so this covers every token we mint.
const TOKEN = '[A-Za-z0-9_-]+'

export const ADMIN_PATH = '/admin'
export const TEMPLATES_PATH = '/admin/templates'
export const DISCOVERY_PATH = '/discovery'
// Where the sign-in card lives. It renders on any signed-in path a signed-out
// visitor opens, but when a page has to send someone to it (a spent verify
// link) this is the canonical address. Principals arrive by personal link and
// never need it.
export const SIGN_IN_PATH = ADMIN_PATH

export const ROLE_ADMIN = 'admin'
export const ROLE_PRINCIPAL = 'principal'

function stripSlash(pathname) {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

function tokenFrom(pathname, prefix) {
  const match = pathname.match(new RegExp(`^/${prefix}/(${TOKEN})/?$`))
  return match ? match[1] : null
}

// --- Public shapes ---------------------------------------------------------------

export function linkToken(pathname) {
  return tokenFrom(pathname, 's')
}

export function verifyToken(pathname) {
  return tokenFrom(pathname, 'verify')
}

// The principal's personal link. The shape is fixed here so the onboarding
// email (services/access.py build_personal_link) and PrincipalLinkPage agree.
export function principalToken(pathname) {
  return tokenFrom(pathname, 'p')
}

// --- Signed-in surfaces ----------------------------------------------------------

export function isAdminPath(pathname) {
  return pathname === ADMIN_PATH || pathname.startsWith(`${ADMIN_PATH}/`)
}

export function isDiscoveryPath(pathname) {
  return pathname === DISCOVERY_PATH || pathname.startsWith(`${DISCOVERY_PATH}/`)
}

// Anything that fetches /me before rendering.
export function isSignedInPath(pathname) {
  return isAdminPath(pathname) || isDiscoveryPath(pathname)
}

export function isTemplatesPath(pathname) {
  return stripSlash(pathname) === TEMPLATES_PATH
}

// The space and the link id, never the link token: the token is the
// participant's credential for the public /s/ page and has no business in a
// signed-in URL. Every link route lives under its space, so the URL carries the
// space too. The attempt is part of the URL so a second call links to its own
// result rather than to whatever is newest; it is optional, and absent means
// "the latest". Mirrored server-side by notify.result_url.
const RESULT_RE = new RegExp(`^/admin/spaces/(${UUID})/results/(${UUID})(?:/(\\d+))?/?$`, 'i')

export function resultPath(spaceId, linkId, attempt = null) {
  const base = `/admin/spaces/${spaceId}/results/${linkId}`
  return attempt ? `${base}/${attempt}` : base
}

// { spaceId, linkId, attempt } from an admin result URL, or null for any other
// path. A malformed tail returns null on purpose — an unparseable URL lands on
// the list rather than firing a request that can only 404.
export function resultRoute(pathname) {
  const match = pathname.match(RESULT_RE)
  if (!match) return null
  return { spaceId: match[1], linkId: match[2], attempt: match[3] ? Number(match[3]) : null }
}

export function spacePath(spaceId) {
  return `${DISCOVERY_PATH}/${spaceId}`
}

export function spaceResultsPath(spaceId) {
  return `${spacePath(spaceId)}/results`
}

export function spaceResultPath(spaceId, linkId, attempt = null) {
  const base = `${spaceResultsPath(spaceId)}/${linkId}`
  return attempt ? `${base}/${attempt}` : base
}

const DISCOVERY_RE = new RegExp(
  `^/discovery(?:/(${UUID})(?:/(results)(?:/(${UUID})(?:/(\\d+))?)?)?)?/?$`,
  'i',
)

// A space as the principal sees it. An admin may open it too (allowedPath):
// the access card links here, in a new tab.
export function discoverySpacePath(spaceId) {
  return `${DISCOVERY_PATH}/${spaceId}`
}

// { spaceId, results, linkId, attempt } from a discovery URL, or null for any
// other path. `/discovery` itself parses to all-null fields: the home.
export function discoveryRoute(pathname) {
  const match = pathname.match(DISCOVERY_RE)
  if (!match) return null
  return {
    spaceId: match[1] ?? null,
    results: match[2] !== undefined,
    linkId: match[3] ?? null,
    attempt: match[4] ? Number(match[4]) : null,
  }
}

// --- Roles -------------------------------------------------------------------------

export function homePath(role) {
  return role === ROLE_PRINCIPAL ? DISCOVERY_PATH : ADMIN_PATH
}

// May someone with this role be shown this path? This is the allow-list of
// shapes the app ever routes to, and the role rule in one place: a principal
// never lands on /admin…, whichever URL they typed, followed or had remembered
// for them. Anything not listed — including a value planted in storage — is
// refused, so this can never become a redirect primitive.
export function allowedPath(pathname, role) {
  const path = stripSlash(pathname)
  if (isAdminPath(path)) {
    if (role !== ROLE_ADMIN) return false
    return (
      path === ADMIN_PATH ||
      isTemplatesPath(path) ||
      resultRoute(path) !== null ||
      adminSpaceRoute(path) !== null
    )
  }
  if (isDiscoveryPath(path)) {
    const route = discoveryRoute(path)
    if (!route) return false
    // A principal watches states, never what was said: the results shapes
    // under /discovery are an admin's view of a space from the outside.
    return !route.results || role === ROLE_ADMIN
  }
  return false
}

// The admin's view of a space and its three tabs. People is the default, so it
// gets the bare path; Results and Settings are path segments rather than a
// query string, so they are Back-able and shareable like the result URL above
// (a bare /results is the tab; /results/<link_id> is one write-up). Distinct
// from the principal's /discovery/<space_id>, which is the same space seen
// from outside.
const ADMIN_SPACE_RE = new RegExp(`^/admin/spaces/(${UUID})(?:/(results|settings))?/?$`, 'i')

export function adminSpacePath(spaceId, tab = 'people') {
  const base = `/admin/spaces/${spaceId}`
  return tab === 'people' ? base : `${base}/${tab}`
}

// { spaceId, tab } from an admin space URL, or null for any other path.
export function adminSpaceRoute(pathname) {
  const match = pathname.match(ADMIN_SPACE_RE)
  if (!match) return null
  return { spaceId: match[1], tab: match[2] ? match[2].toLowerCase() : 'people' }
}

// --- Where to land after signing in --------------------------------------------

// Following the receipt email's link with a lapsed session shows the login
// screen, and the magic link that follows usually opens in a NEW TAB — so the
// destination has to outlive this tab. localStorage, not sessionStorage.
// One-shot, and short-lived: MAGIC_LINK_TTL_MINUTES (60) is as long as the link
// itself lives.
const DEST_KEY = 'capston.postLoginPath'
const DEST_TTL_MS = 60 * 60 * 1000

export function rememberPath(path) {
  // A home is never worth remembering: the sign-in already knows each role's,
  // and remembering one would send an admin who logged out on /discovery back
  // there every time.
  const home = stripSlash(path)
  if (home === ADMIN_PATH || home === DISCOVERY_PATH) return
  try {
    window.localStorage.setItem(DEST_KEY, JSON.stringify({ path, at: Date.now() }))
  } catch {
    // Private mode / storage disabled: sign-in still works, it just lands on
    // the role's home.
  }
}

// Consumes the remembered destination whether or not it is returned, so a
// path stored for one person can never redirect a later sign-in.
export function takeRememberedPath(role) {
  let raw = null
  try {
    raw = window.localStorage.getItem(DEST_KEY)
    window.localStorage.removeItem(DEST_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const { path, at } = JSON.parse(raw)
    if (typeof path !== 'string' || typeof at !== 'number') return null
    if (Date.now() - at > DEST_TTL_MS) return null
    // Validated, not trusted: the same allow-list and role rule as every other
    // path change, so a value planted in storage can never become a redirect
    // primitive or hand a principal an admin page.
    return allowedPath(path, role) ? path : null
  } catch {
    return null
  }
}
